/**
 * API client configuration.
 *
 * Uses generated OpenAPI services where available, with Axios fallback.
 *
 * Type Generation:
 *   TypeScript types are auto-generated from FastAPI's OpenAPI spec.
 *   Run `npm run generate:api` to regenerate after backend changes.
 *   Generated types are in src/lib/generated/ (git-ignored).
 *
 * Usage:
 *   import { api, apiClient } from '@/lib/api';
 *   import type { TradeRecommendation, Position } from '@/lib/generated';
 *
 * Migration Note:
 *   This file migrates from manual axios calls to generated services.
 *   Generated services return T directly, but apiClient methods wrap
 *   responses in { data: T } to maintain backward compatibility.
 */
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';

import { heartbeatManager } from '@/lib/heartbeat';

// Import generated services
import { ApiError } from './generated/core/ApiError';
import { OpenAPI } from './generated/core/OpenAPI';
import { AuthService } from './generated/services/AuthService';
import { PositionsService } from './generated/services/PositionsService';
import { PerformanceService } from './generated/services/PerformanceService';
import { WatchlistService } from './generated/services/WatchlistService';
import { ShadowBookService } from './generated/services/ShadowBookService';
import { EarningsAlphaService } from './generated/services/EarningsAlphaService';
import { EstimatesService } from './generated/services/EstimatesService';
import {
  analyzeRollPosition,
  analyzeRollPositionsBatch,
  getRollWeights,
  recordRollOutcome,
  updateRollWeights,
} from './rollAnalyzerApi';
import type { ContagionAnalysis } from './generated/models/ContagionAnalysis';
import type { ContagionSignalSummary } from './generated/models/ContagionSignalSummary';
import type { EstimateCreate } from './generated/models/EstimateCreate';
import type { RecordOutcomeRequest } from './generated/models/RecordOutcomeRequest';
import type { RollAnalyzerRequest } from './generated/models/RollAnalyzerRequest';
import type { UpdateWeightsRequest } from './generated/models/UpdateWeightsRequest';
import type { WatchlistSource } from './generated/models/WatchlistSource';

// Base URL - in dev, Vite proxies /api to FastAPI
export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Configure OpenAPI client to use credentials (for auth cookies)
OpenAPI.BASE = API_BASE_URL;
OpenAPI.WITH_CREDENTIALS = true;
OpenAPI.CREDENTIALS = 'include';

/**
 * Canonical builder for a Job SSE stream URL.
 * Replaces the hardcoded `/api/v1/jobs/{id}/stream` string scattered across hooks.
 * Defaults to a RELATIVE path (nginx proxies /api) so it is a drop-in for useSSE;
 * pass { absolute: true } to prefix API_BASE_URL when a full origin is required.
 */
export function jobStreamUrl(
  jobId: string,
  opts?: {
    replay?: boolean;
    clientId?: string;
    absolute?: boolean;
    baseUrl?: string;
    params?: Record<string, string | number | boolean | null | undefined>;
  },
): string {
  const params = new URLSearchParams();
  if (opts?.replay !== undefined) params.set('replay', opts.replay ? 'true' : 'false');
  if (opts?.clientId) params.set('client_id', opts.clientId);
  for (const [key, value] of Object.entries(opts?.params ?? {})) {
    if (value != null) params.set(key, String(value));
  }
  const qs = params.toString();
  const base = opts?.baseUrl ?? (opts?.absolute ? API_BASE_URL : '');
  return `${base}/api/v1/jobs/${jobId}/stream${qs ? `?${qs}` : ''}`;
}

// Create Axios instance for endpoints not yet in generated services
// Default timeout of 60s handles most operations
// Specific endpoints (scanner, trade-generator) set longer timeouts per-call
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased from 30s to handle long operations
  withCredentials: true, // Include cookies in requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Whether a 401 on this request should trigger a hard redirect to /login.
 *
 * A hard `window.location` navigation tears down the SPA, which corrupts any
 * in-progress job/SSE state. We therefore do NOT redirect when:
 *  - the request is a job-lifecycle endpoint (stream, jobs polling, or
 *    research-workflow cancel/audits/iterate) — a transient 401 mid-flight
 *    must not nuke the job
 *  - ANY job is actively streaming (heartbeat manager has active jobs) — a
 *    401 on an unrelated background request (e.g. a positions refetch) must
 *    not tear down the SPA mid-job either
 *  - we are already on the /login page (avoids a redirect loop)
 * Callers still receive the rejected promise and handle auth via the auth store.
 */
function shouldRedirectOn401(url = ''): boolean {
  // Job streaming + lifecycle endpoints: never redirect mid-flight.
  if (/\/(stream|jobs\/|research-workflow\/)/.test(url)) return false;
  // A job is actively streaming — don't hard-navigate out from under it.
  if (heartbeatManager.getActiveJobs().length > 0) return false;
  // Already on the login page — nothing to redirect to.
  if (typeof window !== 'undefined' && window.location.pathname === '/login') return false;
  return true;
}

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle specific error codes
    if (error.response?.status === 401 && shouldRedirectOn401(error.config?.url ?? '')) {
      // Unauthorized - redirect to login (guarded to avoid clobbering SSE/job state)
      window.location.href = '/login';
    }

    if (error.response?.status === 403) {
      // Forbidden - show upgrade prompt
      console.error('Access denied. Pro subscription required.');
    }

    return Promise.reject(error);
  }
);

/**
 * Extract a human-readable error message from an Axios error or unknown throwable.
 */
export function extractApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (isGeneratedApiError(err)) {
    return extractDetailMessage(err.body?.detail) || err.message || fallback;
  }
  if (axios.isAxiosError(err)) {
    return extractDetailMessage(err.response?.data?.detail) || err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function extractDetailMessage(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail;
  if (!Array.isArray(detail)) return undefined;

  const messages = detail
    .map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && 'msg' in item) {
        const msg = (item as { msg?: unknown }).msg;
        return typeof msg === 'string' ? msg : undefined;
      }
      return undefined;
    })
    .filter((message): message is string => Boolean(message));

  return messages.length > 0 ? messages.join(', ') : undefined;
}

function isGeneratedApiError(err: unknown): err is ApiError {
  return (
    err instanceof ApiError ||
    (
      err instanceof Error &&
      err.name === 'ApiError' &&
      typeof (err as { status?: unknown }).status === 'number'
    )
  );
}

function handleGeneratedApiError(error: unknown): never {
  if (isGeneratedApiError(error)) {
    if (error.status === 401 && shouldRedirectOn401(error.url)) {
      window.location.href = '/login';
    }

    if (error.status === 403) {
      console.error('Access denied. Pro subscription required.');
    }
  }

  throw error;
}

// Helper to wrap generated service responses in AxiosResponse-like object
// This maintains backward compatibility with existing callers that use .data
function wrapResponse<T>(data: T): AxiosResponse<T> {
  return {
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {} as InternalAxiosRequestConfig,
  };
}

async function wrapGeneratedResponse<T>(request: Promise<T>): Promise<AxiosResponse<T>> {
  try {
    return wrapResponse(await request);
  } catch (error) {
    handleGeneratedApiError(error);
  }
}

// Analytics response types
interface SectorDetail {
  delta_notional: number;
  market_value?: number;
  tickers?: string[];
  pct_of_total: number;
}

interface SectorExposureResponse {
  sectors: Record<string, SectorDetail>;
  total_notional: number;
}

interface PortfolioGreeksDetail {
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
}

interface PortfolioGreeksResponse {
  total: PortfolioGreeksDetail & { theta_annual: number; theta_pct_annual: number };
  by_underlying: Record<string, PortfolioGreeksDetail>;
  by_sector: Record<string, PortfolioGreeksDetail>;
  portfolio_value: number;
}

export interface EarningsSegmentTemplate {
  name: string;
  weight: number;
  key_metrics: string[];
  peer_tickers: string[];
  description?: string;
}

export type EarningsSegmentTemplateResponse =
  | {
      ticker: string;
      company_name: string;
      has_template: true;
      primary_thesis: string;
      segments: EarningsSegmentTemplate[];
      risk_factors?: string[];
      total_weight?: number;
    }
  | {
      ticker: string;
      has_template: false;
      company_name?: string;
      primary_thesis?: string;
      segments: EarningsSegmentTemplate[];
      available_tickers?: string[];
      message?: string;
    };

type RawContagionSignal = Partial<ContagionSignalSummary> & {
  source?: string;
  segment?: string | null;
  result?: string;
  type?: string;
};

type RawContagionAnalysis = Partial<Omit<ContagionAnalysis, 'top_signals'>> & {
  top_signals?: RawContagionSignal[];
};

type RawSegmentTemplate = Partial<EarningsSegmentTemplate> & {
  description?: string;
};

type RawSegmentTemplateResponse = {
  ticker?: string;
  company_name?: string;
  has_template?: boolean;
  primary_thesis?: string;
  segments?: RawSegmentTemplate[];
  available_tickers?: string[];
  message?: string;
  risk_factors?: string[];
  total_weight?: number;
};

export const earningsAlphaKeys = {
  all: ['earnings-alpha'] as const,
  contagion: (ticker: string) => [...earningsAlphaKeys.all, 'contagion', ticker] as const,
  segments: (ticker: string) => [...earningsAlphaKeys.all, 'segments', ticker] as const,
};

export const estimateKeys = {
  all: ['estimates'] as const,
  accuracy: () => [...estimateKeys.all, 'accuracy'] as const,
  ticker: (ticker: string) => [...estimateKeys.all, 'ticker', ticker] as const,
};

function normalizeContagionSignal(signal: RawContagionSignal): ContagionSignalSummary {
  return {
    source_ticker: signal.source_ticker ?? signal.source ?? '',
    source_segment: signal.source_segment ?? signal.segment ?? null,
    source_result: signal.source_result ?? signal.result ?? '',
    signal_type: signal.signal_type ?? signal.type ?? 'neutral',
    confidence: signal.confidence ?? 0,
  };
}

function normalizeContagionAnalysis(raw: RawContagionAnalysis): ContagionAnalysis {
  return {
    has_signals: raw.has_signals,
    bullish_count: raw.bullish_count,
    bearish_count: raw.bearish_count,
    neutral_count: raw.neutral_count,
    net_signal: raw.net_signal,
    net_direction: raw.net_direction,
    top_signals: raw.top_signals?.map(normalizeContagionSignal) ?? [],
  };
}

function normalizeSegmentWeight(weight: number | undefined): number {
  if (typeof weight !== 'number' || !Number.isFinite(weight)) return 0;
  const normalized = Math.abs(weight) <= 1 ? weight * 100 : weight;
  return Math.round(normalized * 100) / 100;
}

function normalizeSegmentTemplate(segment: RawSegmentTemplate): EarningsSegmentTemplate {
  return {
    name: segment.name ?? '',
    weight: normalizeSegmentWeight(segment.weight),
    key_metrics: segment.key_metrics ?? [],
    peer_tickers: segment.peer_tickers ?? [],
    description: segment.description,
  };
}

function normalizeSegmentTemplateResponse(raw: RawSegmentTemplateResponse): EarningsSegmentTemplateResponse {
  const ticker = raw.ticker ?? '';

  if (!raw.has_template) {
    return {
      ticker,
      has_template: false,
      segments: [],
      available_tickers: raw.available_tickers,
      message: raw.message,
    };
  }

  return {
    ticker,
    has_template: true,
    company_name: raw.company_name ?? ticker,
    primary_thesis: raw.primary_thesis ?? '',
    segments: raw.segments?.map(normalizeSegmentTemplate) ?? [],
    risk_factors: raw.risk_factors,
    total_weight: raw.total_weight,
  };
}

// API helper functions - uses generated services where available
// Methods return AxiosResponse-like objects for backward compatibility
export const apiClient = {
  // Auth - using generated AuthService
  auth: {
    status: async () => wrapResponse(await AuthService.authStatusAuthStatusGet()),
    me: async () => wrapResponse(await AuthService.getCurrentUserInfoAuthMeGet()),
    logout: async () => wrapResponse(await AuthService.logoutAuthLogoutPost()),
  },

  // Positions - using generated PositionsService
  positions: {
    list: async (params?: { refresh?: boolean; symbol?: string; position_type?: string }) =>
      wrapResponse(await PositionsService.getPositionsApiV1PositionsGet({
        refresh: params?.refresh ?? false,
        symbol: params?.symbol,
        positionType: params?.position_type,
      })),
    summary: async (refresh?: boolean) =>
      wrapResponse(await PositionsService.getPortfolioSummaryApiV1PositionsSummaryGet({ refresh: refresh ?? false })),
    greeks: async (refresh?: boolean) =>
      wrapResponse(await PositionsService.getPortfolioGreeksApiV1PositionsGreeksGet({ refresh: refresh ?? false })),
    get: async (id: number) =>
      wrapResponse(await PositionsService.getPositionApiV1PositionsPositionIdGet({ positionId: id })),
  },

  // Performance - using generated PerformanceService
  performance: {
    get: async (period = 'All', showDeltaNotional = false) =>
      wrapResponse(await PerformanceService.getPerformanceApiV1PerformanceGet({ period, showDeltaNotional })),
    chart: async (period = 'All', showDeltaNotional = false) =>
      wrapResponse(await PerformanceService.getPerformanceChartApiV1PerformanceChartGet({ period, showDeltaNotional })),
    metrics: async (period = 'All') =>
      wrapResponse(await PerformanceService.getPerformanceMetricsApiV1PerformanceMetricsGet({ period })),
    bestWorst: async () =>
      wrapResponse(await PerformanceService.getBestWorstTradesApiV1PerformanceBestWorstGet()),
  },

  // Scanner (inverted pipeline - code finds, LLM reviews)
  // Kill Rule #1: Both endpoints now return { job_id } immediately and run the
  // real work in BackgroundTasks. The actual candidates / expirations arrive via
  // the generic job SSE stream (GET /api/v1/jobs/{job_id}/stream). Consumers must
  // subscribe to the stream and read the result from the `complete` event — they
  // can NOT read candidates/expirations off these POST responses.
  scanner: {
    scan: (config: {
      tickers?: string[];
      strategies?: string[];
      min_reward_risk?: number;
      target_delta?: number;
      min_iv_rank?: number;
      min_dte?: number;
      max_dte?: number;
      use_fallback?: boolean;
      run_review?: boolean;
      earnings_edge_filter?: 'any' | 'sell_premium' | 'buy_premium';
    }) =>
      api.post<{ job_id: string; status: string; message: string }>(
        '/api/v1/trade-generator/scan',
        config,
      ),
    analyzeLongOptions: (config: {
      ticker: string;
      option_type?: string;
      delta_range?: number[];
      max_iv_rank?: number | null;
      max_spread_pct?: number;
    }) =>
      api.post<{ job_id: string; status: string; message: string }>(
        '/api/v1/trade-generator/analyze-long-options',
        config,
      ),
  },

  // User Profile (full user data including usage stats)
  userProfile: {
    get: () => api.get('/api/v1/user/profile'),
  },

  // Shadow Book - using generated ShadowBookService
  shadowBook: {
    list: async (closedLimit = 50) =>
      wrapResponse(await ShadowBookService.getShadowBookApiV1ShadowBookGet({ closedLimit })),
    analytics: async () =>
      wrapResponse(await ShadowBookService.getAnalyticsApiV1ShadowBookAnalyticsGet()),
    get: async (id: number) =>
      wrapResponse(await ShadowBookService.getShadowTradeApiV1ShadowBookShadowIdGet({ shadowId: id })),
    close: async (id: number, data: { exit_price: number; reason?: string }) =>
      wrapResponse(await ShadowBookService.closeShadowTradeApiV1ShadowBookShadowIdClosePost({
        shadowId: id,
        requestBody: data,
      })),
    byTicker: async (ticker: string) =>
      wrapResponse(await ShadowBookService.getShadowTradesByTickerApiV1ShadowBookTickerTickerGet({ ticker })),
  },

  // Prompt Versions (A/B testing - admin only)
  promptVersions: {
    list: () => api.get('/api/admin/prompt-versions'),
    get: (id: number) => api.get(`/api/admin/prompt-versions/${id}`),
    create: (data: { version_name: string; prompt_template: string; description?: string }) =>
      api.post('/api/admin/prompt-versions', data),
    update: (id: number, data: { version_name?: string; prompt_template?: string; description?: string }) =>
      api.put(`/api/admin/prompt-versions/${id}`, data),
    delete: (id: number) => api.delete(`/api/admin/prompt-versions/${id}`),
    activate: (id: number, data: { as_stable: boolean; traffic_pct?: number }) =>
      api.post(`/api/admin/prompt-versions/${id}/activate`, data),
    setTraffic: (id: number, trafficPct: number) =>
      api.post(`/api/admin/prompt-versions/${id}/traffic`, { traffic_pct: trafficPct }),
    retire: (id: number) => api.post(`/api/admin/prompt-versions/${id}/retire`),
    analytics: (id: number) => api.get(`/api/admin/prompt-versions/${id}/analytics`),
    compare: (versionAId: number, versionBId: number) =>
      api.get('/api/admin/prompt-versions/compare', { params: { version_a_id: versionAId, version_b_id: versionBId } }),
    refreshMetrics: (id: number) => api.post(`/api/admin/prompt-versions/${id}/refresh-metrics`),
  },

  // Fundamentals (company financial metrics)
  fundamentals: {
    summary: (ticker: string, refresh = false) =>
      api.get(`/api/v1/fundamentals/${ticker}`, { params: { refresh } }),
    balanceSheet: (ticker: string, refresh = false) =>
      api.get(`/api/v1/fundamentals/${ticker}/balance-sheet`, { params: { refresh } }),
    incomeStatement: (ticker: string, refresh = false) =>
      api.get(`/api/v1/fundamentals/${ticker}/income-statement`, { params: { refresh } }),
  },

  // Roll Analyzer
  rollAnalyzer: {
    analyze: async (data: RollAnalyzerRequest) =>
      wrapGeneratedResponse(analyzeRollPosition(data)),
    batch: async (createSnapshots = true) =>
      wrapGeneratedResponse(analyzeRollPositionsBatch(createSnapshots)),
    recordOutcome: async (data: RecordOutcomeRequest) =>
      wrapGeneratedResponse(recordRollOutcome(data)),
    updateWeights: async (data?: UpdateWeightsRequest | null) =>
      wrapGeneratedResponse(updateRollWeights(data)),
    weights: async (strategyBucket?: string | null) =>
      wrapGeneratedResponse(getRollWeights(strategyBucket)),
  },

  // Earnings Alpha - using generated EarningsAlphaService for stable endpoint contracts
  earningsAlpha: {
    contagion: async (ticker: string) =>
      wrapResponse(normalizeContagionAnalysis(
        await EarningsAlphaService.getContagionSignalsApiV1EarningsAlphaContagionTickerGet({
          ticker,
        }) as RawContagionAnalysis,
      )),
    segments: async (ticker: string) =>
      wrapResponse(normalizeSegmentTemplateResponse(
        await EarningsAlphaService.getSegmentTemplatesApiV1EarningsAlphaTickerSegmentsGet({
          ticker,
        }) as RawSegmentTemplateResponse,
      )),
  },

  // Estimate tracking - using generated EstimatesService
  estimates: {
    accuracy: async () =>
      wrapGeneratedResponse(EstimatesService.getAccuracyStatsApiV1EstimatesAccuracyGet()),
    list: async (ticker: string, limit = 20) =>
      wrapGeneratedResponse(EstimatesService.getEstimatesForTickerApiV1EstimatesTickerGet({
        ticker,
        limit,
      })),
    create: async (ticker: string, data: EstimateCreate) =>
      wrapGeneratedResponse(EstimatesService.createEstimateApiV1EstimatesTickerPost({
        ticker,
        requestBody: data,
      })),
    delete: async (id: number) =>
      wrapGeneratedResponse(EstimatesService.deleteEstimateApiV1EstimatesEstimateIdDelete({
        estimateId: id,
      })),
    recordOutcome: async (id: number, actualValue: number) =>
      wrapGeneratedResponse(EstimatesService.recordOutcomeApiV1EstimatesEstimateIdOutcomePost({
        estimateId: id,
        requestBody: { actual_value: actualValue },
      })),
  },

  // Research Workflow - iterate on completed research
  researchWorkflow: {
    iterate: (data: {
      parent_job_id: string;
      feedback: string;
      locked_phases: string[];
    }) => api.post('/api/v1/research-workflow/iterate', data),
  },

  // Slides (research report slide deck generation)
  slides: {
    generate: (jobId: string) =>
      api.post('/api/v1/slides/generate', { job_id: jobId }),
    status: (slideJobId: string) =>
      api.get(`/api/v1/slides/${slideJobId}/status`),
    download: (slideJobId: string) => {
      // Trigger browser download via window.open
      window.open(`${API_BASE_URL}/api/v1/slides/${slideJobId}/download`, '_blank');
    },
  },

  // Videos (performance video generation)
  videos: {
    generate: (period = 'All') =>
      api.post('/api/v1/videos/performance', { period }),
    list: (limit = 20) =>
      api.get('/api/v1/videos', { params: { limit } }),
    download: (videoId: string) =>
      api.get(`/api/v1/videos/${videoId}/download`, { responseType: 'blob' }),
  },

  // Valuations (DCF, Comps, Screener)
  valuations: {
    // Industry templates for DCF
    templates: () =>
      api.get('/api/v1/valuations/templates'),
    // DCF with smart defaults
    dcf: (ticker: string) =>
      api.get(`/api/v1/valuations/dcf/${ticker}`),
    // DCF with custom assumptions
    dcfCustom: (ticker: string, assumptions: {
      revenue_growth_y1_y3?: number | null;
      revenue_growth_y4_y5?: number | null;
      terminal_growth?: number;
      operating_margin_year1?: number | null;
      target_operating_margin?: number | null;
      capex_as_pct_revenue?: number | null;
      depreciation_as_pct_revenue?: number | null;
      nwc_as_pct_revenue?: number | null;
      risk_free_rate?: number | null;
      equity_risk_premium?: number;
      beta?: number | null;
      custom_wacc?: number | null;
      tax_rate?: number;
      terminal_method?: 'gordon' | 'exit_multiple';
      exit_multiple?: number;
      projection_years?: number;
    }) =>
      api.post(`/api/v1/valuations/dcf/${ticker}`, assumptions),
    // Comps with auto-detected peers
    comps: (ticker: string) =>
      api.get(`/api/v1/valuations/comps/${ticker}`),
    // Comps with custom peers
    compsCustom: (ticker: string, peers: string[]) =>
      api.post(`/api/v1/valuations/comps/${ticker}`, { peers }),
    // Screen stocks by EV/EBITDA vs Growth
    screen: (data: {
      tickers: string[];
      min_revenue_growth?: number;
      max_ev_ebitda?: number;
      min_ev_ebitda?: number;
      min_market_cap?: number;
      max_market_cap?: number | null;
    }) =>
      api.post('/api/v1/valuations/screen', data),
    // Get peers for a ticker
    peers: (ticker: string, count = 5) =>
      api.get(`/api/v1/valuations/peers/${ticker}`, { params: { count } }),
    // Set manual peer override
    setPeers: (ticker: string, peers: string[]) =>
      api.post(`/api/v1/valuations/peers/${ticker}`, { peers }),
    // Delete manual peer override
    deletePeers: (ticker: string) =>
      api.delete(`/api/v1/valuations/peers/${ticker}`),
    // DCF Monte Carlo simulation
    dcfMonteCarlo: (ticker: string, data?: object) =>
      api.post(`/api/v1/valuations/dcf/${ticker}/monte-carlo`, data ?? {}),
    // Combined valuation summary
    summary: (ticker: string, includeDcf = true, includeComps = true) =>
      api.get(`/api/v1/valuations/summary/${ticker}`, {
        params: { include_dcf: includeDcf, include_comps: includeComps },
      }),
  },

  // Analytics - portfolio-level intelligence
  analytics: {
    sectorExposure: () => api.get<SectorExposureResponse>('/api/v1/analytics/sector-exposure'),
    portfolioGreeks: () => api.get<PortfolioGreeksResponse>('/api/v1/analytics/portfolio-greeks'),
  },

  // Trade Doctor - health diagnostics
  tradeDoctor: {
    trends: (days = 14) => api.get('/api/v1/trade-doctor/trends', { params: { days } }),
  },

  // Watchlist - using generated WatchlistService
  watchlist: {
    list: async () =>
      wrapResponse(await WatchlistService.getWatchlistApiV1WatchlistGet()),
    add: async (data: {
      ticker: string;
      notes?: string;
      source?: WatchlistSource;
      research_enabled?: boolean;
    }) => wrapResponse(await WatchlistService.addToWatchlistApiV1WatchlistPost({
      requestBody: data,
    })),
    remove: async (ticker: string) =>
      wrapResponse(await WatchlistService.removeFromWatchlistApiV1WatchlistTickerDelete({ ticker })),
    update: async (
      ticker: string,
      data: { notes?: string; priority?: number; research_enabled?: boolean }
    ) => wrapResponse(await WatchlistService.updateWatchlistItemApiV1WatchlistTickerPatch({
      ticker,
      requestBody: data,
    })),

    // Schedule (Pro only)
    schedule: {
      get: async () =>
        wrapResponse(await WatchlistService.getScheduleConfigApiV1WatchlistScheduleGet()),
      update: async (data: { enabled: boolean; schedule_time?: string }) =>
        wrapResponse(await WatchlistService.updateScheduleConfigApiV1WatchlistSchedulePut({
          requestBody: data,
        })),
    },

    // Research All (Pro only)
    research: {
      start: async (data?: { tickers?: string[]; deep_thinking?: boolean; leaps?: boolean }) =>
        wrapResponse(await WatchlistService.startResearchAllApiV1WatchlistResearchPost({
          requestBody: data ?? {},
        })),
      status: async () =>
        wrapResponse(await WatchlistService.getResearchStatusApiV1WatchlistResearchStatusGet()),
    },
  },
};

export default api;
